/*
 * (c) Copyright, 2024, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Formik, FormikProps } from 'formik'
import { useServiceContext } from '../../root'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import { useWsHistoryTypes } from '../../hooks/useNotifications'
import { persistService } from '../../services/persist'
import { Select } from '../../components/Select/Select'

const styles = {
  fontClass: {
    fontSize: '14px'
  }
}

interface FilterValues {
  webSocketHistoryType: string[]
}

interface FormValues {
  filter: FilterValues
}

interface WsHistoryTypeOption {
  label: string
  value: string
}

const TableFilter: React.FC = () => {
  const context = useServiceContext()
  const tableData = useRcmTableData()
  const { translate } = context.service.filter
  const persistenceKey = tableData.persistenceKey + '_filter'
  const defaultFilter: FilterValues = { webSocketHistoryType: [] }

  const [filter, setFilter] = useState<FilterValues>(() => {
    // Load persisted filter immediately during initialization
    const persisted = persistService.getRouteScoped<FilterValues>(persistenceKey)
    return persisted && persisted.webSocketHistoryType && persisted.webSocketHistoryType.length > 0
      ? persisted
      : { webSocketHistoryType: [] }
  })

  const { data: wsHistoryTypesData, isLoading: isLoadingTypes } = useWsHistoryTypes()
  const [wsHistoryTypes, setWsHistoryTypes] = useState<WsHistoryTypeOption[]>([])

  useEffect(() => {
    if (wsHistoryTypesData && Array.isArray(wsHistoryTypesData)) {
      setWsHistoryTypes(
        wsHistoryTypesData.map((item: string) => ({
          label: translate('WS_HISTORY_TYPE_' + item),
          value: item
        }))
      )
    }
  }, [wsHistoryTypesData])

  const doFilter = (values: FilterValues) => {
    tableData.applyFilter({ ...values })
    // Persist filter when Filter button is clicked
    persistService.setRouteScoped(persistenceKey, { ...values })
  }

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map((option) => option.value)
    return selectedOptions
  }

  // Check if filters are equal (for disabling reset button)
  const isEqual = (obj1: FilterValues, obj2: FilterValues): boolean => {
    return JSON.stringify(obj1) === JSON.stringify(obj2)
  }

  // Collapse component replacement (simple CSS-based)
  const CollapseDiv: React.FC<{ in: boolean; children: React.ReactNode }> = ({ in: isOpen, children }) => {
    return (
      <div
        style={{
          maxHeight: isOpen ? '1000px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.3s ease-in-out'
        }}
      >
        {children}
      </div>
    )
  }

  return (
    <CollapseDiv in={!tableData.isFilterCollapsed}>
      <div className="filter-wrapper">
        <Formik
          initialValues={{
            filter: filter
          }}
          innerRef={(el: FormikProps<FormValues> | null) => {
            if (el) {
              // Store formRef in pageContext store for access in loadData
              const storeRef = context.store as { formRef?: FormikProps<FormValues> }
              if (storeRef) {
                storeRef.formRef = el
              }
            }
          }}
          onReset={() => {
            setFilter({ webSocketHistoryType: [] })
            doFilter({ webSocketHistoryType: [] })
            // Clear persisted filter on reset
            persistService.setRouteScoped(persistenceKey, { webSocketHistoryType: [] })
          }}
          enableReinitialize={true}
          onSubmit={(values) => {
            doFilter(values.filter)
          }}
        >
          {({ values, setFieldValue, handleSubmit, handleReset }) => {
            return (
              <>
                <div className="row">
                  <div className="col-xs-4">
                    <div className="form-group" id="8906bc16-5e36-4300-9607-db92ffb37828">
                      <label style={styles.fontClass}>
                        <strong>{translate('NOTIFICATION_EVENT_TYPE')}</strong>
                      </label>
                      <Select
                        id="webSocketHistoryType"
                        name="filter.webSocketHistoryType"
                        options={wsHistoryTypes}
                        value={
                          values.filter.webSocketHistoryType.length > 0 ? values.filter.webSocketHistoryType : undefined
                        }
                        menuPosition="fixed"
                        multi={true}
                        onChange={(selectedOptions) => {
                          const selectedValues = Array.isArray(selectedOptions)
                            ? selectedOptions.map((option) => option.value)
                            : []
                          setFieldValue('filter.webSocketHistoryType', selectedValues)
                          setFilter({ webSocketHistoryType: selectedValues })
                        }}
                        isDisabled={isLoadingTypes}
                        placeholder={translate('SELECT_EVENT_TYPE_S')}
                      />
                    </div>
                  </div>
                </div>
                <div className="clearfix" style={{ marginTop: '10px' }}>
                  <div className="pull-left">
                    <button
                      type="button"
                      data-action="filter"
                      disabled={tableData.isLoading}
                      className="btn btn-primary"
                      onClick={() => handleSubmit()}
                      id="0c909163-7d90-47f8-afc4-11ff83a82377"
                      style={{ marginRight: '10px' }}
                    >
                      {translate('BTN_FILTER')}
                    </button>
                    <button
                      type="button"
                      data-action="reset"
                      disabled={isEqual(defaultFilter, values.filter)}
                      className="btn btn-link at-reset"
                      onClick={() => handleReset()}
                      id="48b8689c-129a-46f6-bc64-0a39284b13b1"
                      style={{
                        color: '#337ab7',
                        textDecoration: 'none',
                        padding: '6px 12px'
                      }}
                    >
                      {translate('BTN_CLEAR_FILTER')}
                    </button>
                  </div>
                </div>
              </>
            )
          }}
        </Formik>
      </div>
    </CollapseDiv>
  )
}

export default TableFilter
